import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    effect,
    inject,
    untracked,
} from '@angular/core';
import { ID } from '@clean-code/models/common';
import {
    IDashboardWidgetCorrelationsUpdateInput,
    IDashboardWidgetDetailDto,
    IWidgetPositionInfo,
} from '@clean-code/shared/components/ui-dashboard';
import { DashboardFacade } from '@clean-code/shared/dashboard/dashboard/domain';
import {
    CompactType,
    DisplayGrid,
    GridType,
    Gridster,
    GridsterConfig,
    GridsterItem,
    GridsterItemConfig,
} from 'angular-gridster2';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { DashboardPanelWidgetComponent } from '../dashboard-panel-widget/dashboard-panel-widget.component';

@Component({
  selector: 'dashboard-dashboard-panel-widgets',
  templateUrl: './dashboard-panel-widgets.component.html',
  styleUrls: ['./dashboard-panel-widgets.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [Gridster, GridsterItem, DashboardPanelWidgetComponent],
})
export class DashboardPanelWidgetsComponent implements OnInit, OnDestroy {
  @HostBinding('class') class = 'dashboard-dashboard-panel-widgets';

  @ViewChild('gridster', { static: false }) public gridster?: Gridster;

  private _options: GridsterConfig;
  private closeSubject = new Subject<void>();
  private syncGridster$ = new Subject<GridsterItem>();
  @Input()
  public set options(value: GridsterConfig) {
    if (value) {
      this._options = { ...this._options, ...value };
    }
  }

  public get options(): GridsterConfig {
    return this._options;
  }

  public dashboardFacade = inject(DashboardFacade);
  public widgets = this.dashboardFacade.widgets;
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    const editMode = this.dashboardFacade.editMode();
    this._options = this.getDefaultOptions();

    // Initialize with current edit mode state
    this._options = {
      ...this._options,
      draggable: { ...this._options.draggable, enabled: editMode },
      resizable: { ...this._options.resizable, enabled: editMode },
    };

    effect(() => {
      const value = this.dashboardFacade.editMode();

      untracked(() => {
        this._options = {
          ...this._options,
          draggable: { ...this._options.draggable, enabled: value },
          resizable: { ...this._options.resizable, enabled: value },
        };
        this.changedOptions();
        this.cdr.markForCheck();

        if (!value) {
          const payload = this.buildCorrelationsUpdateInput();

          if (payload) {
            this.dashboardFacade.updateAllCorrelations$(payload).subscribe();
          }
        }
      });
    });
  }

  public ngOnInit(): void {
    this.syncGridster$
      .pipe(
        debounceTime(400),
        tap((x: GridsterItem) => this.syncWidgetState(x)),
        takeUntil(this.closeSubject),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.closeSubject.next();
    this.closeSubject.complete();
  }

  public trackByFn(_index: number, widget: IDashboardWidgetDetailDto): ID {
    return widget.id;
  }

  private getDefaultOptions(): GridsterConfig {
    return {
      gridType: GridType.Fit,
      compactType: CompactType.None,
      // pushItems: true,
      displayGrid: DisplayGrid.OnDragAndResize,
      minCols: 50,
      minRows: 50,
      maxCols: 50,
      maxRows: 50,
      defaultItemCols: 5,
      defaultItemRows: 5,
      mobileFitContentHeight: true,
      itemChangeCallback: (
        _item: GridsterItemConfig,
        itemComponent: GridsterItem,
      ) => {
        this.syncGridster$.next(itemComponent);
      },
      draggable: {
        enabled: this.dashboardFacade.editMode(),
        // delayStart: 400,
        // stop: (
        //   _item: GridsterItem,
        //   itemComponent: GridsterItemComponentInterface,
        //   _event: MouseEvent
        // ) => {
        //   // this.syncWidgetState(itemComponent);
        //   this.syncGridster$.next(itemComponent);
        // },
      },
      resizable: {
        enabled: this.dashboardFacade.editMode(),
        // delayStart: 400,
        // stop: (
        //   _item: GridsterItem,
        //   itemComponent: GridsterItemComponentInterface,
        //   _event: MouseEvent
        // ) => {
        //   // this.syncWidgetState(itemComponent);
        //   // this.saveCorrelation(item, itemComponent, event);
        //   this.syncGridster$.next(itemComponent);
        // },
      },
    };
  }

  private syncWidgetState(item: GridsterItem): void {
    if (!item) {
      return;
    }

    const itemConfig = item.$item();
    const originalItem = item.item() as IDashboardWidgetDetailDto;

    if (!originalItem?.id) {
      return;
    }

    const widget: IDashboardWidgetDetailDto = {
      ...originalItem,
      x: itemConfig.x,
      y: itemConfig.y,
      cols: itemConfig.cols,
      rows: itemConfig.rows,
    };

    this.dashboardFacade.updateWidgetPosition(widget.id, widget);
  }

  private buildCorrelationsUpdateInput(): IDashboardWidgetCorrelationsUpdateInput | null {
    const activeDashboard = this.dashboardFacade.active();
    const dashboardId = activeDashboard?.id;
    if (!dashboardId) {
      return null;
    }

    if (!this.gridster?.grid?.length) {
      return null;
    }

    const payload: IDashboardWidgetCorrelationsUpdateInput = {
      dashboardId,
      widgets: [] as IWidgetPositionInfo[],
    };

    this.gridster.grid.forEach((item) => {
      const itemConfig = item.$item();
      const originalItem = item.item() as IDashboardWidgetDetailDto;

      if (!originalItem?.id) {
        return;
      }

      payload.widgets.push({
        id: originalItem.id,
        x: itemConfig.x,
        y: itemConfig.y,
        cols: itemConfig.cols,
        rows: itemConfig.rows,
      });
    });

    return payload;
  }

  private changedOptions(): void {
    this._options = { ...this._options };
  }
}
